import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Chip, useTheme, type ChipProps } from '@mui/material';
import {
  describeStatus,
  statusFamilyColors,
  type StatusDomain,
} from '@/features/status/statusCatalog';

type StatusChipProps = {
  domain: StatusDomain;
  status: string;
  size?: ChipProps['size'];
  sx?: ChipProps['sx'];
};

/**
 * The label of a state, in the color of its family (#285). Screens pass the state; which text
 * and which color it gets is decided once, in `statusCatalog.ts`.
 *
 * An error also carries an icon, so it is not told apart by color alone.
 */
export function StatusChip({ domain, status, size = 'small', sx }: StatusChipProps) {
  const theme = useTheme();
  const { label, family } = describeStatus(domain, status);
  const colors = statusFamilyColors[theme.palette.mode][family];

  return (
    <Chip
      size={size}
      label={label}
      data-status-family={family}
      icon={family === 'error' ? <ErrorOutlineIcon aria-hidden /> : undefined}
      sx={[
        {
          color: colors.text,
          bgcolor: colors.background,
          border: '1px solid',
          borderColor: colors.border,
          fontWeight: 700,
          '& .MuiChip-icon': { color: colors.text },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  );
}
